import Select from "components/form/select";
import Checkbox from "components/form/checkbox";
import Slider from "components/form/slider";
import Filters from "components/filters";
import Dialog from "components/dialog";

import { FiChevronDown } from "react-icons/fi";
import { Filters as FiltersProps } from "types/filters";
import clsx from "clsx";
import { rejectObjByKeys } from "utils";
import { normalizeFilters } from "utils/metadata";
import { isMobile } from "utils/browser";

import { useEffect, useState } from "react";
import { useExam } from "hooks/metadata";
import { useNotifications } from "hooks/notifications";
import { useUser } from "hooks/user";
import { useQuestionCount } from "hooks/questionnaire";
import { useMounted } from "hooks/react";
import { useSubscription } from "hooks/subscription";

interface QuestionnaireFiltersProps {
  isClosable?: boolean;
  loading?: boolean;
  className?: string;
  onSearch: (filters: any) => void;
  onClose: () => void;
}

export default function QuestionnaireFilters(
  props: QuestionnaireFiltersProps
): JSX.Element {
  const initialFilters: FiltersProps = {
    language: "ingles",
    questionCount: 10
  };

  const [filters, setFilters] = useState(initialFilters);
  const { institutions, years, languages, testDays } = useExam();
  const [showTestModeDialog, setShowTestModeDialog] = useState(false);
  const [showExamFilters, setShowExamFilters] = useState(false);
  const [maxQuestions, setMaxQuestions] = useState(100);
  const { isActive: hasActiveSubscription } = useSubscription();

  const questionCountFilters = rejectObjByKeys(filters, [
    "questionCount",
    "tweakedQuestionsCount"
  ]);
  const {
    questionCount: availableQuestions,
    isLoading: isLoadingQuestionCount
  } = useQuestionCount(questionCountFilters, !hasInitialFilters());
  const questionsCountText =
    availableQuestions > 100
      ? "mais de 100 questões"
      : availableQuestions === 1
      ? "1 questões"
      : `${availableQuestions} questões`;

  const hasFullExamSelected = (() => {
    if (filters.subject) return false;

    return (
      filters.institution &&
      filters.year &&
      (filters.institution === "ENEM" ? filters.testDay : true)
    );
  })();

  const { user } = useUser();
  const { setNotification } = useNotifications();

  useMounted(() => {
    if (isMobile()) setShowExamFilters(true);
  });

  useEffect(() => {
    if (showExamFilters) return;

    setFilters({
      ...filters,
      institution: null,
      year: null,
      testDay: null,
      language: "ingles"
    });
  }, [showExamFilters]);

  useEffect(() => {
    if (!availableQuestions) return;
    const newMax = Math.min(availableQuestions, 100);
    setMaxQuestions(newMax);
  }, [availableQuestions]);

  function handleSelect({ name, value }) {
    setFilters({ ...filters, [name]: value });
  }

  function selectInput(config) {
    const { label, name, disabled, nullLabel, callback } = config;
    let options = config.options || [];
    if (nullLabel) options = [{ label: nullLabel, value: null }, ...options];

    return (
      <Select
        onSelect={handleSelect}
        callback={callback}
        label={label}
        name={name}
        value={filters[name] || null}
        options={options}
        disabled={disabled}
        onClickDisabled={config.onClickDisabled}
        className="mb-4"
      />
    );
  }

  function hasInitialFilters() {
    return (
      JSON.stringify(normalizeFilters(filters)) ===
      JSON.stringify(normalizeFilters(initialFilters))
    );
  }

  return (
    <Filters
      {...props}
      onSearch={() => props.onSearch({ ...filters, maxQuestions })}
      onChange={(newFilters) => setFilters({ ...filters, ...newFilters })}
      className={clsx("h-full", props.className)}
      actionText="Gerar simulado"
    >
      <hr className="mx-8 my-10 border-gray-200" />

      <div className="exam-wrapper md:mb-0">
        <span
          className={clsx(
            "flex items-center justify-between mb-4 text-lg cursor-pointer md:mb-0",
            { "md:!mb-4": showExamFilters }
          )}
          onClick={() => !isMobile() && setShowExamFilters(!showExamFilters)}
        >
          Escolha por prova
          <div
            className={clsx(
              "hidden-xs text-emphasis-low transform transition-transform",
              {
                "rotate-180": showExamFilters
              }
            )}
          >
            <FiChevronDown size={20} />
          </div>
        </span>
        {showExamFilters && (
          <div>
            <div className="mb-4">
              {selectInput({
                label: "Instituição",
                name: "institution",
                options: institutions,
                nullLabel: "Todas"
              })}
              {selectInput({
                label: "Ano",
                name: "year",
                options: years,
                nullLabel: "Todos"
              })}
              {selectInput({
                label: "Idioma",
                name: "language",
                options: languages
              })}
              {filters.institution === "ENEM" &&
                selectInput({
                  label: "Dia do teste",
                  name: "testDay",
                  options: testDays,
                  nullLabel: "Ambos"
                })}
            </div>
            <div className="test-mode-checkbox-wrapper flex items-center justify-between mb-6">
              <Checkbox
                label="Modo prova"
                disabled={!hasFullExamSelected}
                onChange={(newValue) =>
                  handleSelect({
                    name: "seriousFrom",
                    value: (newValue && new Date()) || null
                  })
                }
                onClickDisabled={() => {
                  if (!hasActiveSubscription) {
                    return setNotification({
                      title: "Recurso exclusivo para assinantes",
                      text: "O modo prova é exclusivo para assinantes. Assine agora por apenas R$19,90/mês e tenha acesso a todos os recursos! <a href='/assinatura' class='block font-semibold mt-3 w-full text-center'>Assinar agora</a>"
                    });
                  }

                  if (hasFullExamSelected) return;

                  setNotification({
                    title: "Selecione uma prova completa",
                    text: "O modo prova só está disponível quando apenas uma instituição e ano estão selecionados (além do dia da prova, se for ENEM)"
                  });
                }}
              />
              <div
                className="warning-trigger flex items-center justify-center w-5 h-5 bg-primary-400 rounded-full cursor-pointer"
                onClick={() => setShowTestModeDialog(true)}
              >
                <span className="text-sm">?</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <hr className="mx-8 my-10 border-gray-200" />

      {user && (
        <div className="mb-6">
          <Checkbox
            label="Remover questões já respondidas"
            onChange={(newValue) =>
              handleSelect({
                name: "notAnswered",
                value: newValue
              })
            }
          />
        </div>
      )}

      {!hasInitialFilters() && (
        <span
          className={clsx(
            "fade-loading block mb-6 w-full text-center text-emphasis-low",
            { loading: isLoadingQuestionCount }
          )}
        >
          {availableQuestions > 0
            ? `Temos ${questionsCountText} com esses filtros 😀`
            : `Não encontramos questões com esses filtros 😕`}
        </span>
      )}

      {!hasFullExamSelected && (
        <div className="mb-10">
          <label className="block mb-3 text-emphasis-medium">
            Quantidade de questões
          </label>
          <Slider
            initialValue={10}
            min={1}
            max={maxQuestions}
            onChange={(questionCount) => {
              const tweakedQuestionsCount =
                questionCount !== 10 &&
                (maxQuestions > 10 || questionCount !== maxQuestions);
              setFilters({ ...filters, questionCount, tweakedQuestionsCount });
            }}
          />
        </div>
      )}

      {showTestModeDialog && (
        <Dialog
          title="Modo prova"
          confirmText="Entendi!"
          onCancel={() => setShowTestModeDialog(false)}
        >
          <p className="dialog-text">
            Resolva uma prova inteira no mesmo tempo do dia da prova e sem ver
            as respostas antes de terminar de resolver o simulado.
          </p>
        </Dialog>
      )}
    </Filters>
  );
}
